import { useEffect, useState } from 'react';
import { firebaseAuth } from '@/lib/firebase';
import { needsEmailVerification, sendVerificationEmail } from '@/lib/auth';

/** Soft, dismissible reminder shown to email/password users who haven't yet
    confirmed their email. Never blocks access (verification is soft) — Google
    and already-verified users never see it. */
export default function EmailVerificationBanner() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged(() => {
      setShow(needsEmailVerification());
    });
    return unsub;
  }, []);

  if (!show || dismissed) return null;

  const resend = async () => {
    setBusy(true);
    setNote('');
    try {
      await sendVerificationEmail();
      setNote('Verification email sent — check your inbox (and spam).');
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not send the email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-3 text-sm">
      <i className="ri-mail-check-line text-amber-600" />
      <span className="text-amber-800 flex-1">
        {note || 'Please verify your email address to secure your account.'}
      </span>
      <button
        type="button"
        onClick={resend}
        disabled={busy}
        className="text-amber-800 font-semibold hover:text-amber-900 whitespace-nowrap cursor-pointer disabled:opacity-60"
      >
        {busy ? 'Sending…' : 'Resend link'}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-amber-500 hover:text-amber-700 cursor-pointer"
        title="Dismiss"
        aria-label="Dismiss"
      >
        <i className="ri-close-line" />
      </button>
    </div>
  );
}
